import { z } from 'zod';
import { centavos } from './envelope.ts';
import {
  playerStatus,
  kycStatus,
  playerTier,
  restrictionKind,
  kycSubmissionStatus,
  bonusGrantStatus,
  bonusGrantSource,
  promotionKind,
} from './lists.ts';
import type { DeclaredNameParts } from './player-auth.ts';

/**
 * `GET /api/players/:id` — the single biggest concentration of schema-less
 * wire types on the platform before this file: one route, eight response
 * shapes, money AND permissions AND player PII all in one payload (md-gp-32
 * rank 1). Authored from the route handler itself
 * (`services/api/src/modules/players/routes.ts:503`), not from either FE
 * mirror of it — there are three independent hand-rollings of pieces of this
 * response (`apps/back-office/src/features/profile/types.ts`,
 * `players/types.ts`, `funds/Withdrawals.tsx`) and none of them was treated
 * as the source of truth.
 *
 * The route names every column it reads — it no longer says `SELECT p.*` —
 * and they cross the wire raw, snake_case, exactly as each column or alias is
 * named: there is no transform at this boundary, so every key of
 * `playerRecord` below is snake_case too. That is deliberate, not an
 * oversight: describing the wire as camelCase when the route sends snake_case
 * would be a contract that is wrong from the day it is authored. The one
 * object inside it, `declared_name`, is built by the route's own
 * `json_build_object` with camelCase keys, the shape `formatDeclaredName`
 * takes, and is declared here as the route builds it.
 *
 * CR (md-gp-36) was written when the route was `SELECT p.*`, and its concern
 * now runs the other way round: a column added to `player.player` reaches
 * this endpoint only when the route's SELECT names it, and a key the route
 * sends that this schema does not declare is stripped by `z.object` with
 * nothing to say so. `players-profile-money.integration.test.ts` compares
 * the route's keys with this schema's in both directions, so a column added
 * to one and not the other fails there.
 */
export const playerRecord = z.object({
  id: z.string(),
  ref: z.string(),
  brand_id: z.string(),
  display_name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  status: playerStatus,
  kyc_status: kycStatus,
  tier: playerTier,
  risk_score: z.number().int(),
  agent_ref: z.string().nullable(),
  date_of_birth: z.iso.datetime().nullable(),
  registered_at: z.iso.datetime(),
  last_seen_at: z.iso.datetime().nullable(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
  /**
   * ISO 3166-1 alpha-2, or NULL for an account that predates the column
   * (`player.player.residence_country`, added in `sql/290_player_residency.sql`
   * — NULL is "never asked", not "unknown country").
   */
  residence_country: z.string().nullable(),
  /**
   * `player.player.marketing_consent_at`/`marketing_consent_withdrawn_at`
   * (`sql/370_marketing_consent.sql`) — named in the route's SELECT like every
   * other column, but declared in NEITHER of the three FE mirrors of this route.
   * Not part of any card yet; recorded here because the schema describes
   * what the route sends, not what a stale FE type happened to ask for.
   */
  marketing_consent_at: z.iso.datetime().nullable(),
  marketing_consent_withdrawn_at: z.iso.datetime().nullable(),
  brand_name: z.string(),
  /** Centavos, from `wallet.player_cash_balance()` — evaluated per request. */
  cash_balance: centavos,
  /**
   * The three LIFETIME figures, computed by this route with the SAME SQL the
   * roster list uses, so a profile cannot disagree with the row it was opened
   * from about the same question.
   */
  lifetime_deposits: centavos,
  lifetime_withdrawals: centavos,
  lifetime_ggr: centavos,
  /**
   * An approved test account, whose play is excluded from Gross Gaming Revenue —
   * PAGCOR item 33(c), sql/1003. Named in the route's SELECT like the rest;
   * declared here so the profile can label the control by which way it goes
   * rather than showing a toggle whose state it cannot read.
   */
  is_test: z.boolean(),
  /** Why, in the words of the person who approved it. Null when not marked. */
  test_reason: z.string().nullable(),
  test_approved_at: z.iso.datetime().nullable(),
  /**
   * The name the player gave at sign-up (`player.declared_name`, sql/1018), in
   * the four parts `formatDeclaredName` takes — not a column of `player.player`,
   * kept apart from it because it is a legal name.
   *
   * NULL TWO WAYS: for a reader the identity case showing the same name would
   * turn away — it is behind `kyc.review`, decided by `mayExercise` — and for an
   * account opened before sign-up asked for a name. `declared_name_shown` says
   * which. A one-name player has no first name, middle name or suffix: the one
   * name is `lastName`.
   */
  declared_name: z
    .object({
      firstName: z.string().nullable(),
      middleName: z.string().nullable(),
      lastName: z.string(),
      suffix: z.string().nullable(),
    })
    .nullable() satisfies z.ZodType<DeclaredNameParts | null>,
  /**
   * Whether this reader was sent the name: true when the route looked for it —
   * the reader could exercise `kyc.review` at the moment of the read, by the
   * rule the identity case applies (`mayExercise`: the key held and, were it
   * ever to require one, a second factor) — so a NULL `declared_name` means no
   * name was asked at sign-up; false when it was withheld. The page decides from
   * this, never from its own copy of the reader's permissions: that copy is read
   * at sign-in while the route reads them on every request, and a role edited
   * mid-session made the page say "No name was asked at sign-up" of every
   * player who had given one.
   */
  declared_name_shown: z.boolean(),
  /**
   * THE LATEST CORRECTION OF THE NAME AND DATE OF BIRTH, made after a refusal because the details
   * did not match (sql/1023, read through `player.declared_details`): the day it was made, on the
   * Manila calendar, and what it changed — the corrected name in the parts `declared_name` has, and
   * the corrected date of birth (`YYYY-MM-DD`), each null where it changed nothing.
   *
   * NULL when the player has corrected nothing, and for a reader `declared_name` is withheld from:
   * sent by the same rule (`declared_name_shown` says which).
   */
  corrected_details: z
    .object({
      corrected_on: z.string(),
      name: z
        .object({
          firstName: z.string().nullable(),
          middleName: z.string().nullable(),
          lastName: z.string(),
          suffix: z.string().nullable(),
        })
        .nullable() satisfies z.ZodType<DeclaredNameParts | null>,
      date_of_birth: z.string().nullable(),
    })
    .nullable(),
});
export type PlayerRecord = z.infer<typeof playerRecord>;

/**
 * One row of `promo.bonus_grant`, joined to the promotion that made it, as
 * this route reads it — NOT the same shape as any request schema keyed by
 * `bonusGrantReason`/`bonusGrantSource` elsewhere in this file; those are
 * write-side. `bonusGrantSource`'s values were checked against the real
 * writers (`promotions/grants.ts`) before reuse here, not assumed from a
 * docstring — see md-gp-32's reconciliation doc.
 */
export const bonusGrantRow = z.object({
  id: z.string(),
  ref: z.string(),
  amount: centavos,
  status: bonusGrantStatus,
  wagering_required: centavos,
  wagering_completed: centavos,
  /**
   * Hundredths of a percent, or NULL when `wagering_required` is 0 — there is
   * no denominator, and a bar drawn over a zero requirement would read as
   * "cleared" for a grant whose requirement was never worked out.
   *
   * Deliberately UNCAPPED, unlike `GET /api/promotions/grants` and the
   * player's own rewards screen, which both clamp with `least(10000, …)`. A
   * clamp turns 546% into "100%", and "100%" beside an ACTIVE status reads as
   * "cleared, release the payout" — the one claim this figure must not make.
   */
  progress_bp: z.string().nullable(),
  expires_at: z.iso.datetime().nullable(),
  created_at: z.iso.datetime(),
  closed_at: z.iso.datetime().nullable(),
  /** Whether the grant's money was ever posted to the bonus wallet. */
  funded: z.boolean(),
  source: bonusGrantSource.nullable(),
  granted_by_name: z.string().nullable(),
  promotion_name: z.string(),
  promotion_kind: promotionKind,
  /** Hundredths of a multiple: "2000" is 20×. */
  wagering_multiplier: z.string().nullable(),
});
export type BonusGrantRow = z.infer<typeof bonusGrantRow>;

/**
 * One row of `points.points_ledger`, as `GET /api/points/players/:id` returns
 * it (`services/api/src/modules/backoffice/catalogue.ts:1042`) — a different
 * route from the rest of this file, gated the same way (`players.view`).
 */
export const pointsEntry = z.object({
  id: z.string(),
  /** Signed. Positive is earned, negative is spent. Points, never pesos. */
  points: z.string(),
  reason: z.string().nullable(),
  source_ref: z.string().nullable(),
  created_at: z.iso.datetime(),
});
export type PointsEntry = z.infer<typeof pointsEntry>;

export const pointsLedger = z.object({
  items: z.array(pointsEntry),
  balance: z.string(),
  earned: z.string(),
  spent: z.string(),
  entryCount: z.number().int().nonnegative(),
  /** True when the fifty-row cap is hiding older entries. */
  truncated: z.boolean(),
});
export type PointsLedger = z.infer<typeof pointsLedger>;

/**
 * What an early payout — sorry, what a WITHDRAWAL may actually draw on,
 * computed by this route with the SAME arithmetic `GET /api/player/wallet`
 * and `POST /api/player/withdrawals` use, so the console reads the same
 * number the player is reading.
 *
 * `available` cannot go negative on purpose: money can leave by another path
 * after a request was raised, and "−₱400 available" is a figure with no
 * meaning.
 */
export const withdrawableFigures = z.object({
  cash: centavos,
  heldInOpenRequests: centavos,
  openRequests: z.number().int().nonnegative(),
  available: centavos,
});
export type WithdrawableFigures = z.infer<typeof withdrawableFigures>;

/**
 * The two non-cash balances, and the two figures gated on `transactions.view`.
 *
 * `adjustments`/`withdrawable` are NULL, not an absent key and not an empty
 * array, when the caller lacks `transactions.view` — "may not read this" and
 * "there is none" are different facts, and the profile prints a different
 * sentence for each. A missing capability is a null here, never a 403: this
 * panel is one of eight on the page, and refusing the whole response over one
 * of them would take the rest down for a Support Agent who holds
 * `players.view` and nothing stronger.
 */
export const profileBalances = z.object({
  /** Centavos in the player's PLAYER_BONUS account, summed from the ledger. */
  bonusLocked: centavos,
  /** A COUNT, not money. Never formatted as pesos. */
  pointsBalance: z.string(),
  /**
   * One row of `wallet.manual_adjustment` for this player, as this route
   * returns it. NOT `adjustmentRow` (lists.ts) — that is the cross-player
   * CHECKER QUEUE shape (`playerId`/`playerRef`/`brandName` on every row,
   * because that list spans every player); this is the single-player PANEL
   * shape, and the page already knows whose page it is. Two different
   * shapes for two different screens, on purpose (§E of the reconciliation
   * doc) — a mechanical swap onto `adjustmentRow` would bolt five identity
   * fields onto a page that does not need them.
   */
  adjustments: z
    .array(
      z.object({
        id: z.string(),
        ref: z.string(),
        /** CREDIT · DEBIT — the direction against the PLAYER's balance. */
        direction: z.string(),
        amount: centavos,
        /** A free varchar with no reference table: read as stored, not mapped. */
        reason_code: z.string(),
        reason_note: z.string().nullable(),
        /** AWAITING_CHECKER · APPROVED · REJECTED. */
        status: z.string(),
        initiated_by: z.string(),
        maker_label: z.string().nullable(),
        approved_by: z.string().nullable(),
        checker_label: z.string().nullable(),
        approved_at: z.iso.datetime().nullable(),
        rejected_reason: z.string().nullable(),
        transaction_id: z.string().nullable(),
        transaction_ref: z.string().nullable(),
        created_at: z.iso.datetime(),
      }),
    )
    .nullable(),
  withdrawable: withdrawableFigures.nullable(),
});
export type ProfileBalances = z.infer<typeof profileBalances>;

/**
 * The whole profile response.
 *
 * `restrictions`/`notes`/`kycSubmissions` are declared HERE, inline, in the
 * route's actual (snake_case) casing — NOT as `restrictionRow`/
 * `kycSubmissionRow`/`playerNoteRow` (lists.ts), which already exist for
 * this exact endpoint (their own docstrings say "as the profile reads it")
 * but describe the CAMELCASE TARGET, not what the route sends TODAY.
 * Reusing them here would assert a shape this route does not yet produce —
 * the same mistake in the other direction from restating a shape that
 * already exists. Converging onto them is md-gp-64 (a joint FE+BE
 * migration, because back office reads these fields directly off the raw
 * response); this schema describes the wire as it actually is, and gets
 * replaced in the same commit that lands md-gp-64, not before.
 */
export const playerDetail = z.object({
  player: playerRecord,
  restrictions: z.array(
    z.object({
      id: z.string(),
      kind: restrictionKind,
      active: z.boolean(),
      limit_amount: centavos.nullable(),
      applied_by: z.enum(['PLAYER', 'STAFF', 'SYSTEM']),
      applied_by_ref: z.string().nullable(),
      reason: z.string().nullable(),
      effective_from: z.iso.datetime(),
      expires_at: z.iso.datetime().nullable(),
      lifted_at: z.iso.datetime().nullable(),
      /**
       * Computed by the route with the SAME predicate the withdrawal release
       * applies (`active AND (expires_at IS NULL OR expires_at > now())`), so
       * this screen and the money path cannot disagree about the same row.
       * `restrictionRow` (lists.ts) does not declare this field either —
       * a second, smaller gap in the same schema, folded into md-gp-64.
       */
      in_force: z.boolean(),
    }),
  ),
  notes: z.array(
    z.object({
      id: z.string(),
      body: z.string(),
      author_id: z.string(),
      author_label: z.string(),
      created_at: z.iso.datetime(),
    }),
  ),
  /**
   * The older KYC submissions — HISTORY since that process closed on 23 October
   * 2026. Nothing writes one any more; the KYC tab lists them read-only.
   *
   * The row as stored, and nothing computed: the two fields that were (whether
   * the player held a verified identity record, and how many documents a
   * submission named) existed only for the approve control and the documents
   * panel, and both are gone.
   */
  kycSubmissions: z.array(
    z.object({
      id: z.string(),
      ref: z.string(),
      status: kycSubmissionStatus,
      /** Provider confidence, as stored. Above 0.92 the platform auto-passes. */
      confidence: z.string().nullable(),
      provider_ref: z.string().nullable(),
      rejection_reason: z.string().nullable(),
      resubmit_by: z.iso.datetime().nullable(),
      reviewed_by: z.string().nullable(),
      reviewed_by_name: z.string().nullable(),
      reviewed_at: z.iso.datetime().nullable(),
      submitted_at: z.iso.datetime(),
    }),
  ),
  bonusGrants: z.array(bonusGrantRow),
  balances: profileBalances,
});
export type PlayerDetail = z.infer<typeof playerDetail>;
